import type { AlertFactory } from '@/features/shell'
import type { Document } from '@/features/documents/Document'
import type {
  DocumentStore,
  UploadDocumentProgress,
  UploadDocumentOptions,
} from '@/features/documents/DocumentStore'
import { action, computed, observable, runInAction } from 'mobx'
import { interval, NEVER } from 'rxjs'
import { switchMap, take, tap } from 'rxjs/operators'
import { toStream } from '@/utils/mobx-util'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import {
  assertValidFileSize,
  assertValidFileType,
  validateFileExtension,
} from '@/utils/file-util'

export type UploadBarState = 'hidden' | 'dragging' | 'uploading' | 'resolved'

export interface SuccessfulUploadDocRes {
  success: true
  file: File
  doc: Document
}

interface ErrorUploadDocRes {
  success: false
  file: File
  error: unknown
}

export type UploadDocResult = SuccessfulUploadDocRes | ErrorUploadDocRes

export interface UploadBarViewModelOptions {
  /**
   * Whether the bar hides itself a while after the uploads resolve.
   */
  autoDismiss?: boolean
}

/**
 * View model for validating and uploading files one at a time while
 * reporting the progress in an upload bar.
 */
export class UploadBarViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Progress of the documents whose contents are being uploaded.
   */
  private readonly _documentUploads = observable.map<
    Document,
    UploadDocumentProgress
  >()

  @observable accessor uploadBarState: UploadBarState = 'hidden'
  @observable accessor uploadQueue = new Set<File>()
  @observable accessor uploadQueueTotal = 0
  @observable accessor currentlyUploadingFile: File | null = null
  @observable accessor successUploads: SuccessfulUploadDocRes[] = []

  readonly uploadBarState$ = toStream(() => this.uploadBarState, true)

  /**
   * Whether the bar hides itself a while after the uploads resolve.
   */
  readonly autoDismiss: boolean

  constructor(
    private readonly _newAlert: AlertFactory,
    private readonly _documentStore: DocumentStore,
    { autoDismiss = false }: UploadBarViewModelOptions = {},
  ) {
    this.autoDismiss = autoDismiss
    if (autoDismiss) {
      // When the upload state becomes resolved, start a timer that
      // transitions to the hidden state.
      this._lifecycle.manage(
        this.uploadBarState$.pipe(
          switchMap((state) =>
            state === 'resolved' ? interval(5000).pipe(take(1)) : NEVER,
          ),
          tap(this.setHidden),
        ),
      )
    }
  }

  @computed
  get uploadQueueFiles() {
    return Array.from(this.uploadQueue)
  }

  @action.bound
  setUploadBarState(barState: UploadBarState) {
    this.uploadBarState = barState
  }

  @computed
  get uploadPercent() {
    return (
      ((this.uploadQueueTotal - this.uploadQueueFiles.length) /
        this.uploadQueueTotal) *
      100
    )
  }

  @computed
  get docsPendingUpload() {
    return this.uploadQueueFiles.length - 1
  }

  /**
   * How much of the document's contents have been uploaded, in percent, or
   * `null` when the document isn't being uploaded here.
   */
  documentUploadPercent(document: Document): number | null {
    const progress = this._documentUploads.get(document)
    if (!progress) {
      return null
    }

    return progress.total
      ? Math.ceil((progress.uploaded / progress.total) * 100)
      : 0
  }

  activate() {
    this._lifecycle.activate()
  }

  deactivate() {
    this._lifecycle.deactivate()
  }

  @action.bound
  reset() {
    this.uploadQueue.clear()
    this.uploadQueueTotal = 0
    this.currentlyUploadingFile = null
    this.successUploads = []
  }

  @action.bound
  setCurrentlyUploadingFile(file: File) {
    this.currentlyUploadingFile = file
  }

  async uploadFiles(requests: UploadDocumentOptions[]) {
    this._startUploading(requests)

    for (const request of requests) {
      this.setCurrentlyUploadingFile(request.file)
      const res = await this._uploadDocument(request)

      if (!res.success) {
        this._newAlert().fromError(res.error).show()
      }
      this._finishUpload(res)
    }

    this.setResolved()
  }

  @action.bound
  setUploading() {
    this.setUploadBarState('uploading')
  }

  @computed
  get uploading() {
    return this.uploadBarState === 'uploading'
  }

  @action.bound
  setDragging() {
    this.setUploadBarState('dragging')
  }

  @computed
  get dragging() {
    return this.uploadBarState === 'dragging'
  }

  @action.bound
  setHidden() {
    this.setUploadBarState('hidden')
  }

  @computed
  get hidden() {
    return this.uploadBarState === 'hidden'
  }

  @action.bound
  setResolved() {
    this.setUploadBarState('resolved')
  }

  @computed
  get resolved() {
    return this.uploadBarState === 'resolved'
  }

  /**
   * Shows the drop target when files are dragged in, unless an upload is
   * still in progress.
   */
  @action.bound
  dragEnter() {
    if (this.hidden || this.resolved) {
      this.setDragging()
    }
  }

  /**
   * Hides the drop target when files are dragged out, unless an upload is
   * still in progress.
   */
  @action.bound
  dragLeave() {
    if (!this.uploading) {
      this.setHidden()
    }
  }

  /**
   * Validates the file, then uploads it. Never rejects; failures are
   * reported in the result so one bad file doesn't stop the rest of the
   * queue.
   */
  private async _uploadDocument(
    request: UploadDocumentOptions,
  ): Promise<UploadDocResult> {
    const { file } = request
    try {
      validateFileExtension(file)
      assertValidFileType(file)
      assertValidFileSize(file)

      const upload = this._documentStore.upload(request)
      void upload.added
        .then(async (document) => {
          await upload.progress$.forEach(
            action((progress) => this._documentUploads.set(document, progress)),
          )
          runInAction(() => this._documentUploads.delete(document))
        })
        // `upload.completed` reports the failure.
        .catch(() => undefined)

      const doc = await upload.completed
      return { success: true, file, doc }
    } catch (error) {
      return { success: false, file, error }
    }
  }

  @action.bound
  private _startUploading(requests: UploadDocumentOptions[]) {
    this.reset()
    this.setUploading()
    requests.forEach((r) => this.uploadQueue.add(r.file))
    this.uploadQueueTotal = requests.length
  }

  @action.bound
  private _finishUpload(res: UploadDocResult) {
    if (res.success) {
      this.successUploads.push(res)
    }
    this.uploadQueue.delete(res.file)
  }
}
